import React from 'react';
import SEO from '../components/layout/SEO';
import { FileText, Shield, Users, CreditCard, AlertTriangle, CheckCircle } from 'lucide-react';

const Terms: React.FC = () => {
  const sections = [
    {
      icon: Users,
      title: 'Acceptance of Terms',
      content: 'By accessing and using tadriso LMS, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.'
    },
    {
      icon: Shield,
      title: 'Use License',
      content: 'Permission is granted to temporarily download one copy of the materials (information or software) on tadriso LMS for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.'
    },
    {
      icon: AlertTriangle,
      title: 'Disclaimer',
      content: 'The materials on tadriso LMS are provided on an \'as is\' basis. tadriso LMS makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.'
    },
    {
      icon: CheckCircle,
      title: 'Limitations',
      content: 'In no event shall tadriso LMS or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on tadriso LMS.'
    }
  ];

  const userResponsibilities = [
    'Maintain the confidentiality of your account credentials',
    'Use the service in compliance with applicable laws',
    'Not attempt to gain unauthorized access to the system',
    'Not use the service for any illegal or unauthorized purpose',
    'Report any security vulnerabilities to our team',
    'Respect the intellectual property rights of others'
  ];

  const serviceTerms = [
    {
      title: 'Subscription Services',
      description: 'tadriso LMS offers various subscription plans. Fees are billed in advance on a monthly or annual basis. You may cancel your subscription at any time through your account settings.'
    },
    {
      title: 'Data Retention',
      description: 'We retain your data for as long as your account is active or as needed to provide services. Upon account deletion, data is permanently removed within 30 days.'
    },
    {
      title: 'Service Availability',
      description: 'We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. We reserve the right to perform maintenance that may temporarily affect service availability.'
    },
    {
      title: 'Updates and Changes',
      description: 'We may update these terms from time to time. We will notify users of any material changes via email or through the service interface.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Terms of Service - tadriso LMS"
        description="Read the terms of service for tadriso LMS. Understand your rights and responsibilities when using our platform."
        keywords="terms of service, user agreement, legal terms, tadriso LMS"
      />
      
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Terms of
              <span className="text-primary-600 block">Service</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Please read these terms carefully before using tadriso LMS. By using our service, you agree to these terms.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Main Terms */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <section.icon size={24} className="text-primary-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Responsibilities */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            User Responsibilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {userResponsibilities.map((responsibility, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle size={20} className="text-green-500 mt-1 flex-shrink-0" />
                <p className="text-gray-600">{responsibility}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Terms */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Service Terms
          </h2>
          <div className="space-y-8">
            {serviceTerms.map((term, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {term.title}
                </h3>
                <p className="text-gray-600">
                  {term.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Questions About These Terms?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            If you have any questions about these Terms of Service, please contact our legal team.
          </p>
          <div className="space-y-4">
            <p className="text-primary-100">
              <strong>Email:</strong> legal@ciphermind.tech
            </p>
            <p className="text-primary-100">
              <strong>Address:</strong> Lahore, Pakistan
            </p>
            <p className="text-primary-100">
              <strong>Phone:</strong> +92 (308) 7700430
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms; 